import * as THREE from 'three';
import { FIXED_TONES } from './figure';

/**
 * The crowd's shared Lambert material, patched so one InstancedMesh can draw many looks:
 *
 * - colour per vertex tone (figure.ts): channel A = instanceColor, B = iColB, C = iColC (per-instance
 *   attributes), tones ≥ 3 = fixed colours; times the vertex shade;
 * - variants: vertices tagged aVar ≠ the instance's iVar collapse to a point (no fragments), so all
 *   torso cuts / faces / hats / shoes of a kind share one draw call;
 * - hair: four per-vertex blend deltas mixed with the instance's iHair weights.
 *
 * The matching depth material applies the same variant collapse so shadows match.
 */
export interface CrowdMaterialOptions {
  b?: boolean;
  c?: boolean;
  variant?: boolean;
  hair?: boolean;
}

const fixed = FIXED_TONES.map((h) => new THREE.Color(h));

function defines(o: CrowdMaterialOptions): string {
  return `${o.b ? '#define CROWD_B\n' : ''}${o.c ? '#define CROWD_C\n' : ''}${o.variant ? '#define CROWD_VAR\n' : ''}${o.hair ? '#define CROWD_HAIR\n' : ''}`;
}

const DECL = /* glsl */ `
attribute vec2 aTone;
#ifdef CROWD_B
attribute vec3 iColB;
#endif
#ifdef CROWD_C
attribute vec3 iColC;
#endif
#ifdef CROWD_VAR
attribute float aVar;
attribute float iVar;
#endif
#ifdef CROWD_HAIR
attribute vec3 aD0;
attribute vec3 aD1;
attribute vec3 aD2;
attribute vec3 aD3;
attribute vec4 iHair;
#endif
uniform vec3 uFixed[${FIXED_TONES.length}];
`;

const SHAPE = /* glsl */ `
#ifdef CROWD_HAIR
transformed += aD0 * iHair.x + aD1 * iHair.y + aD2 * iHair.z + aD3 * iHair.w;
#endif
#ifdef CROWD_VAR
if (aVar > -0.5 && ((int(aVar + 0.5) >> int(iVar + 0.5)) & 1) == 0) transformed = vec3(0.0);
#endif
`;

const COLOUR = /* glsl */ `
vec3 crowdCol = vec3(1.0);
#ifdef USE_INSTANCING_COLOR
crowdCol = instanceColor.rgb;
#endif
float crowdCh = aTone.x;
#ifdef CROWD_B
if (crowdCh > 0.5 && crowdCh < 1.5) crowdCol = iColB;
#endif
#ifdef CROWD_C
if (crowdCh > 1.5 && crowdCh < 2.5) crowdCol = iColC;
#endif
if (crowdCh > 2.5) crowdCol = uFixed[int(crowdCh - 2.5)];
vColor = vec4( 1.0 );
vColor.rgb = crowdCol * aTone.y;
`;

export function createCrowdMaterial(o: CrowdMaterialOptions): { material: THREE.MeshLambertMaterial; depth: THREE.MeshDepthMaterial } {
  const key = `crowd:${o.b ? 1 : 0}${o.c ? 1 : 0}${o.variant ? 1 : 0}${o.hair ? 1 : 0}`;
  const material = new THREE.MeshLambertMaterial({ color: 0xffffff });
  const uFixed = { value: fixed };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFixed = uFixed;
    shader.vertexShader = defines(o) + shader.vertexShader.replace('#include <common>', `#include <common>\n${DECL}`).replace('#include <begin_vertex>', `#include <begin_vertex>\n${SHAPE}`).replace('#include <color_vertex>', COLOUR);
  };
  material.customProgramCacheKey = () => key;
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  depth.onBeforeCompile = (shader) => {
    shader.uniforms.uFixed = uFixed;
    shader.vertexShader = defines({ variant: o.variant, hair: o.hair }) + shader.vertexShader.replace('#include <common>', `#include <common>\n${DECL}`).replace('#include <begin_vertex>', `#include <begin_vertex>\n${SHAPE}`);
  };
  depth.customProgramCacheKey = () => `${key}:depth`;
  return { material, depth };
}
